import React, { useEffect, useRef } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Clapperboard, LockKeyhole, Play, RefreshCw } from "lucide-react";

import PageWrapper from "../../../components/layout/page/PageWrapper";
import ContentPanel from "../../../components/layout/content/ContentPanel";
import Heading from "../../../components/typography/Heading";
import Text from "../../../components/typography/Text";

import EmptyState from "../../../components/feedback/EmptyState";
import Button from "../../../components/buttons/Button";
import BackButton from "../../../components/buttons/back/BackButton";

import TalentPromo from "../../home/components/promo/TalentPromo";
import VideoPlayer from "../components/videos/VideoPlayer";
import VideoRentalPanel from "../components/videos/rentals/VideoRentalPanel";

import { useVideoDetails } from "../users/hooks/useVideos";
import { useVideoRental } from "../users/hooks/useVideoRental";
import { useVideoPlayback } from "../users/hooks/useVideoPlayback";

import "./VideoWatchPage.css";

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default function VideoWatchPage() {
  const { videoTag } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const checkoutStatus = searchParams.get("checkout");
  const requestedPlaybackRef = useRef(false);

  const {
    video,
    loading: loadingVideo,
    error: videoError,
  } = useVideoDetails(videoTag);

  const {
    access,
    loading: loadingAccess,
    renting,
    error: rentalError,
    checkAccess,
    rentVideo,
  } = useVideoRental(videoTag);

  const canWatch =
    video?.isFree === true ||
    video?.canWatch === true ||
    access?.canWatch === true ||
    access?.hasAccess === true;

  const {
    playback,
    loading: loadingPlayback,
    error: playbackError,
    loadPlayback,
  } = useVideoPlayback(videoTag, canWatch);

  const creatorBrandName =
    video?.brandName?.trim() || video?.username?.trim() || "";

  const creatorProfileUrl = video?.username
    ? `/${encodeURIComponent(video.username)}`
    : null;

  const playbackUrl =
    playback?.url || playback?.playbackUrl || playback?.videoUrl || null;

  const playbackType = playback?.type || playback?.playbackType || "mp4";

  const rentalRequired = !loadingAccess && !canWatch;
  const accessUnconfirmed = Boolean(rentalError);

  const videoMeta = video
    ? [video.competition, video.round, formatDate(video.createdAt)]
        .filter(Boolean)
        .join(" · ")
    : "";

  const goBack = () => {
    navigate(-1, { replace: true });
  };

  useEffect(() => {
    requestedPlaybackRef.current = false;
  }, [videoTag, canWatch]);

  useEffect(() => {
    if (!canWatch || loadingAccess || requestedPlaybackRef.current) {
      return;
    }

    requestedPlaybackRef.current = true;
    void loadPlayback();
  }, [canWatch, loadingAccess, loadPlayback]);

  useEffect(() => {
    if (checkoutStatus !== "success") return;

    void checkAccess();
  }, [checkoutStatus, checkAccess]);

  useEffect(() => {
    document.title = video?.title
      ? `${video.title} | Katch`
      : "Watch Video | Katch";
  }, [video?.title]);

  const renderPreview = () => {
    if (canWatch && playbackUrl) {
      return (
        <VideoPlayer
          src={playbackUrl}
          poster={playback?.poster || video.thumbnailUrl}
          title={video.title}
          type={playbackType}
          onRefresh={loadPlayback}
        />
      );
    }

    return (
      <div className="katch-watch-preview">
        {video.thumbnailUrl && (
          <img
            src={video.thumbnailUrl}
            alt=""
            className="katch-watch-preview__image"
          />
        )}

        <div className="katch-watch-preview__overlay">
          {loadingAccess || loadingPlayback ? (
            <div className="katch-watch-preview__message" role="status">
              <RefreshCw
                size={28}
                className="katch-watch-spin"
                aria-hidden="true"
              />

              <Text as="p" size="sm">
                {loadingAccess
                  ? "Checking rental access..."
                  : "Preparing playback..."}
              </Text>
            </div>
          ) : rentalRequired ? (
            <div className="katch-watch-preview__message">
              <div className="katch-watch-preview__icon">
                <LockKeyhole size={28} aria-hidden="true" />
              </div>

              <Heading as="h2" size="md">
                {accessUnconfirmed ? "Access unavailable" : "Unlock Video"}
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                {accessUnconfirmed
                  ? "Unable to verify your rental access."
                  : "Unlock this match replay with a 5-day rental."}
              </Text>
            </div>
          ) : (
            <div className="katch-watch-preview__message">
              <div className="katch-watch-preview__icon">
                <Play size={28} fill="currentColor" aria-hidden="true" />
              </div>

              <Heading as="h2" size="md">
                Match replay
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                Your video is ready to load.
              </Text>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loadingVideo) {
    return (
      <PageWrapper>
        <div className="katch-watch-loading" role="status">
          <RefreshCw
            size={28}
            className="katch-watch-spin"
            aria-hidden="true"
          />

          <Text as="p" tone="secondary" size="sm">
            Loading video...
          </Text>
        </div>
      </PageWrapper>
    );
  }

  if (videoError || !video) {
    return (
      <PageWrapper>
        <div className="katch-watch-empty">
          <EmptyState
            icon={<Clapperboard size={28} />}
            title="Match unavailable"
            description={videoError || "This match could not be found."}
            action={
              <Button
                value="Browse matches"
                variant="primary-outlined"
                onClick={() => navigate("/videos")}
              />
            }
          />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="katch-watch">
        <div className="katch-watch__navigation">
          <BackButton onClick={goBack} label="Back" />
        </div>

        <div className="katch-watch-layout">
          <main className="katch-watch-layout__main">
            <div className="katch-watch-player">{renderPreview()}</div>

            {canWatch && playbackError && (
              <div className="katch-watch-error" role="alert">
                <Text as="p" size="sm">
                  {playbackError}
                </Text>

                <Button
                  value="Retry playback"
                  icon={<RefreshCw size={16} />}
                  variant="primary-outlined"
                  onClick={loadPlayback}
                  disabled={loadingPlayback}
                />
              </div>
            )}

            <ContentPanel className="katch-watch-details">
              <Heading as="h1" size="lg">
                {video.title}
              </Heading>

              {creatorBrandName && (
                <div className="katch-watch-details__creator">
                  {creatorProfileUrl ? (
                    <Link
                      to={creatorProfileUrl}
                      className="katch-watch-details__creator-link"
                    >
                      {creatorBrandName}
                    </Link>
                  ) : (
                    <Text as="span" size="sm">
                      {creatorBrandName}
                    </Text>
                  )}
                </div>
              )}

              {videoMeta && (
                <Text as="p" tone="secondary" size="sm">
                  {videoMeta}
                </Text>
              )}

              {video.description && (
                <Text
                  as="p"
                  tone="secondary"
                  size="sm"
                  className="katch-watch-details__description"
                >
                  {video.description}
                </Text>
              )}
            </ContentPanel>

            <TalentPromo variant="compact" />
          </main>

          <aside className="katch-watch-layout__aside">
            <div className="katch-watch-rental">
              <VideoRentalPanel
                video={video}
                access={access}
                loading={loadingAccess}
                renting={renting}
                error={rentalError}
                onRent={rentVideo}
                onRetry={checkAccess}
              />
            </div>

            {canWatch && !playbackUrl && !loadingPlayback && (
              <div className="katch-watch-layout__action">
                <Button
                  value="Load Video"
                  icon={<Play size={17} />}
                  variant="primary"
                  onClick={loadPlayback}
                />
              </div>
            )}
          </aside>
        </div>
      </div>
    </PageWrapper>
  );
}
